import React, { useState, useEffect, useRef } from "react";

// =====================================================================
// FellowshipRegistration Page
// MAHARISHI Secretariat International Young Research Fellowship
//
// 1. REGISTRATION LINK: paste your Google Form link into GOOGLE_FORM_URL.
//    The "Register" pill in the quick-nav scrolls to the Register section
//    (placed right below "Important Conditions"), and the big button there
//    opens your Google Form in a new tab.
//
// 2. MOBILE FIXES:
//    - Reveal animation no longer pushes content off-screen (this was the
//      cause of sideways scrolling / cut-off content on phones). Slide
//      distance is smaller on mobile and the page clips horizontal overflow
//      (overflow-x: clip keeps the sticky nav working).
//    - Removed the negative rootMargin that stopped elements from ever
//      becoming "visible" on narrow screens.
//    - Quick-nav is a single swipeable row on mobile instead of wrapping
//      into 3 lines and covering the screen.
//    - Smaller paddings / font sizes on phones, grids use min(…, 100%) so
//      they never overflow, long emails wrap.
//
// 3. HEADER: the real <header> height is measured at runtime so nothing
//    hides under it. If your header isn't a <header> tag, change the
//    querySelector below.
// =====================================================================

const GOOGLE_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLSf5mlV3ACTHxgDtq9iqs4D9S7f0CnslJsW44kuzrWCIgHFDdg/viewform?usp=header"; // <-- replace this
const HEADER_HEIGHT = 85; // fallback only

const prefersReducedMotion =
  typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;

// Finds how many pixels at the top of the screen are covered by YOUR WEBSITE'S
// header, whatever tag/class it uses. It checks what is actually drawn at the
// top edge of the screen (fixed / sticky bars, or an absolute header sitting
// over the page) and returns the lowest edge of it. Returns 0 if nothing
// covers the top (e.g. the header is a normal in-flow element).
function getSiteHeaderBottom(root) {
  if (typeof document === "undefined" || !document.elementsFromPoint) return 0;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let bottom = 0;

  [0.1, 0.5, 0.9].forEach((f) => {
    document.elementsFromPoint(Math.round(vw * f), 2).forEach((el) => {
      if (root && root.contains(el)) return; // ignore this page's own elements
      let node = el;
      while (node && node !== document.body && node !== document.documentElement) {
        const pos = window.getComputedStyle(node).position;
        if (pos === "fixed" || pos === "sticky") {
          const r = node.getBoundingClientRect();
          if (r.top <= 4 && r.width >= vw * 0.5 && r.height <= vh * 0.4) {
            bottom = Math.max(bottom, r.bottom);
          }
          break;
        }
        node = node.parentElement;
      }
    });
  });

  // Transparent/overlay headers that are position:absolute at the page top
  const hdr = document.querySelector("header, .site-header, #header, [role='banner']");
  if (hdr && !(root && root.contains(hdr))) {
    const r = hdr.getBoundingClientRect();
    if (window.getComputedStyle(hdr).position === "absolute" && r.top <= 4 && r.bottom > 0) {
      bottom = Math.max(bottom, r.bottom);
    }
  }
  return Math.max(0, Math.round(bottom));
}

// Simple hook: true when screen width is below the breakpoint
function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

function Reveal({ children, delay = 0, x = 60, style = {}, as = "div" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(prefersReducedMotion);
  const Tag = as;
  const isMobile = useIsMobile();
  const distance = isMobile ? Math.min(x, 16) : x;

  useEffect(() => {
    if (prefersReducedMotion) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.05, rootMargin: "0px 0px -20px 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateX(0)" : `translateX(${distance}px)`,
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s`,
        willChange: "opacity, transform",
        ...style,
      }}
    >
      {children}
    </Tag>
  );
}

const quickFacts = [
  { icon: "📅", label: "Duration", value: "3 months (Dec 2026 – Feb 2027)" },
  { icon: "💰", label: "Total Support", value: "USD 10,000 per participant" },
  { icon: "📍", label: "Venue", value: "ICAR–IIMR, Hyderabad, India" },
];

const quickNav = [
  { id: "about", label: "About" },
  { id: "eligibility", label: "Eligibility" },
  { id: "training", label: "Training Areas", short: "Training" },
  { id: "conditions", label: "Conditions" },
  { id: "register", label: "Register", highlight: true },
];

export default function FellowshipRegistration() {
  const isMobile = useIsMobile();
  const [headerHeight, setHeaderHeight] = useState(HEADER_HEIGHT);
  const [navHeight, setNavHeight] = useState(52);
  const navRef = useRef(null);
  const navOuterRef = useRef(null);
  const navPlaceholderRef = useRef(null);
  const pageRef = useRef(null);

  useEffect(() => {
    const findHeader = () =>
      document.querySelector("header, .site-header, #header, [role='banner']");

    // Full header height + nav height. Only measured on load/resize (never
    // while scrolling) so the layout can't jump.
    const measureLayout = () => {
      // Only measure at the very top of the page, where the header is at its
      // full size, so the hero's top spacing never changes while scrolling.
      if (window.scrollY < 5) {
        const h = getSiteHeaderBottom(pageRef.current);
        setHeaderHeight((prev) => (prev === h ? prev : h));
      }
      if (navOuterRef.current) {
        const n = Math.round(navOuterRef.current.offsetHeight);
        setNavHeight((prev) => (prev === n ? prev : n));
      }
    };

    // Sticky nav, done in JavaScript so it works on every device and inside
    // any site layout (CSS "position: sticky" silently stops working if any
    // parent element has overflow set). Once the nav's natural spot scrolls
    // up to the bottom edge of the site header, it is pinned right under it
    // with position: fixed; a placeholder keeps the page from jumping.
    // - header fixed/sticky  -> nav sits exactly under it (no gap, no overlap)
    // - header scrolls away  -> nav pins to the very top
    // - header hides/shrinks -> nav follows it
    const syncNav = () => {
      const nav = navOuterRef.current;
      const ph = navPlaceholderRef.current;
      if (!nav || !ph) return;
      const bottom = getSiteHeaderBottom(pageRef.current);
      const stuck = ph.getBoundingClientRect().top <= bottom;
      if (stuck) {
        ph.style.height = nav.offsetHeight + "px";
        nav.style.position = "fixed";
        nav.style.top = bottom + "px";
        nav.style.left = "0";
        nav.style.right = "0";
        nav.style.boxShadow = "0 4px 14px rgba(0,0,0,0.08)";
      } else {
        ph.style.height = "";
        nav.style.position = "";
        nav.style.top = "";
        nav.style.left = "";
        nav.style.right = "";
        nav.style.boxShadow = "";
      }
    };

    let raf = 0;
    let stopAt = 0;
    const loop = () => {
      syncNav();
      raf = performance.now() < stopAt ? requestAnimationFrame(loop) : 0;
    };
    // run a short animation-frame loop after any scroll/resize so header
    // transitions (hide / shrink) are followed frame by frame
    const kick = () => {
      stopAt = performance.now() + 800;
      if (!raf) raf = requestAnimationFrame(loop);
    };
    const onResize = () => {
      measureLayout();
      kick();
    };

    measureLayout();
    kick();
    const t1 = setTimeout(onResize, 300); // after fonts/images settle
    const t2 = setTimeout(onResize, 1200);
    const t3 = setTimeout(onResize, 2500); // late-rendering headers

    window.addEventListener("scroll", kick, { passive: true });
    window.addEventListener("resize", onResize);

    const header = findHeader();
    let ro;
    if (header && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(onResize);
      ro.observe(header);
    }
    if (header) header.addEventListener("transitionend", kick);

    return () => {
      window.removeEventListener("scroll", kick);
      window.removeEventListener("resize", onResize);
      if (header) header.removeEventListener("transitionend", kick);
      if (ro) ro.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Smooth-scroll so the section heading lands just below the site header
  // (only if that header is fixed/sticky) and the sticky nav.
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const headerOffset = getSiteHeaderBottom(pageRef.current);
    const top = el.getBoundingClientRect().top + window.scrollY - headerOffset - navHeight - 16;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  };

  // Responsive style tweaks
  const heroStyle = {
    ...styles.hero,
    paddingTop: headerHeight + (isMobile ? 32 : 50),
    paddingBottom: isMobile ? 64 : 60,
    paddingLeft: isMobile ? 16 : 24,
    paddingRight: isMobile ? 16 : 24,
  };
  const containerStyle = {
    ...styles.container,
    padding: isMobile ? "12px 16px 56px" : "20px 20px 80px",
  };
  const sectionStyle = { ...styles.section, marginBottom: isMobile ? 36 : 48 };
  const sectionTitleStyle = {
    ...styles.sectionTitle,
    fontSize: isMobile ? "20px" : "23px",
  };
  const paragraphStyle = {
    ...styles.paragraph,
    fontSize: isMobile ? "15px" : "15.5px",
    lineHeight: isMobile ? 1.7 : 1.75,
  };

  return (
    <div ref={pageRef} style={styles.page}>
      {/* Hero */}
      <section style={heroStyle}>
        <div style={styles.heroPatternTop} />
        <div style={styles.heroPatternBottom} />
        <div style={styles.heroInner}>
          <Reveal delay={0}>
            <p style={styles.heroEyebrow}>Call for Proposals</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 style={styles.heroTitle}>Training Programme on Millets under MAHARISHI</h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p style={{ ...styles.heroSubtitle, fontSize: isMobile ? "14.5px" : "16px" }}>
              Global Centre of Excellence (GCoE) on Millets, ICAR–Indian Institute of Millets
              Research (ICAR–IIMR), Hyderabad, India
            </p>
          </Reveal>
        </div>
      </section>

      {/* Quick facts strip */}
      <div style={{ ...styles.factsStripOuter, padding: isMobile ? "0 16px" : "0 20px" }}>
        <div
          style={{
            ...styles.factsStrip,
            marginTop: isMobile ? "-32px" : "-38px",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          {quickFacts.map((f, i) => (
            <Reveal
              key={i}
              delay={i * 0.08}
              x={30}
              style={{ flex: isMobile ? "none" : "1 1 210px", width: isMobile ? "100%" : "auto" }}
            >
              <div style={styles.factCard}>
                <div style={styles.factIcon}>{f.icon}</div>
                <div>
                  <p style={styles.factLabel}>{f.label}</p>
                  <p style={styles.factValue}>{f.value}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Quick nav — sticks under the site header on every screen size. The
          pinning is handled by syncNav() in the effect above. */}
      <div ref={navPlaceholderRef}>
      <div
        ref={navOuterRef}
        style={{
          ...styles.quickNavOuter,
          padding: isMobile ? "0 12px" : "0 20px",
        }}
      >
        <div
          ref={navRef}
          style={{
            ...styles.quickNav,
            justifyContent: "center",
            flexWrap: "wrap",
            gap: isMobile ? "6px" : "8px",
            padding: isMobile ? "8px 0" : "10px 0",
          }}
        >
          {quickNav.map((n) => (
            <button
              key={n.id}
              style={{
                ...styles.quickNavPill,
                ...(n.highlight ? styles.quickNavPillHighlight : {}),
                fontSize: isMobile ? "12px" : "13px",
                padding: isMobile ? "6px 12px" : "8px 16px",
                minHeight: isMobile ? 32 : "auto",
              }}
              onClick={() => scrollTo(n.id)}
            >
              {isMobile && n.short ? n.short : n.label}
            </button>
          ))}
        </div>
      </div>
      </div>

      <div style={containerStyle}>
        {/* About */}
        <section id="about" style={sectionStyle}>
          <Reveal>
            <p style={paragraphStyle}>
              The MAHARISHI Secretariat invites applications from eligible young researchers
              from African countries to participate in a specialized three-month training
              programme on millet research and related technologies, to be conducted at the
              Global Centre of Excellence (GCoE) on Millets, ICAR–IIMR, Hyderabad, India.
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <p style={paragraphStyle}>
              The GCoE, ICAR–IIMR, Hyderabad, undertakes research on the following mandate
              crops: sorghum, pearl millet, finger millet, foxtail millet, kodo millet, proso
              millet, barnyard millet, little millet and browntop millet.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p style={paragraphStyle}>
              The programme aims to strengthen technical and research capacities, facilitate
              knowledge and technology exchange, and promote institutional collaboration
              between India and African countries in the areas of millet research, crop
              improvement, production, processing, value addition, nutrition,
              entrepreneurship and sustainable millet-based food systems.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p style={paragraphStyle}>
              The training will provide exposure to advanced research approaches and
              technologies relevant to millet improvement, including genomics-led breeding,
              high-throughput genotyping and phenotyping, rapid generation advancement,
              biofortification and development of improved, climate-resilient and
              nutritionally enhanced millet varieties.
            </p>
          </Reveal>
        </section>

        {/* Eligibility */}
        <section id="eligibility" style={sectionStyle}>
          <Reveal>
            <h2 style={sectionTitleStyle}>Eligibility</h2>
          </Reveal>
          <div style={styles.checklistGrid}>
            {[
              "Be a citizen of an African country engaged in a field of millet agriculture research and development.",
              "Must have a PhD in life sciences including biotechnology, plant breeding and genetics, with a relevant academic/professional background.",
              "Be not more than 45 years of age on the date of submission of the application.",
              "Be able to participate in the entire duration of the training programme.",
              "Preference may be given to applicants who can apply and disseminate the knowledge gained in their home countries.",
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.06} x={30}>
                <div style={styles.checkItem}>
                  <span style={styles.checkMark}>✓</span>
                  <span style={styles.checkText}>{item}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Venue */}
        <section style={sectionStyle}>
          <Reveal>
            <h2 style={sectionTitleStyle}>Venue</h2>
          </Reveal>
          <Reveal delay={0.05}>
            <p style={paragraphStyle}>
              The training programme will be conducted at the Global Center of Excellence on
              Millets, ICAR–Indian Institute of Millets Research (ICAR–IIMR), Hyderabad,
              Telangana, India.
            </p>
          </Reveal>
        </section>

        {/* Areas of Training */}
        <section id="training" style={sectionStyle}>
          <Reveal>
            <h2 style={sectionTitleStyle}>Areas of Training</h2>
          </Reveal>
          <div style={styles.cardsGrid}>
            {[
              { icon: "🌾", text: "Millet production, improved agronomic practices, breeding and varietal improvement, and climate-resilient production systems." },
              { icon: "🌱", text: "Seed systems and quality seed production." },
              { icon: "📦", text: "Post-harvest management and storage, processing, value addition, and millet-based nutrition and food security." },
              { icon: "🏭", text: "Food product development, entrepreneurship, processing technologies, quality assurance, food safety, and market development." },
              { icon: "🤝", text: "Experience sharing and exposure to India's millet ecosystem." },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.07} x={35}>
                <div style={styles.trainingCard}>
                  <div style={styles.trainingCardIcon}>{item.icon}</div>
                  <p style={styles.trainingCardText}>{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Duration */}
        <section style={sectionStyle}>
          <Reveal>
            <h2 style={sectionTitleStyle}>Duration</h2>
          </Reveal>
          <Reveal delay={0.05}>
            <p style={paragraphStyle}>
              The proposed training will be of three months, starting 1st December 2026 till
              28th February 2027 (tentative). Exact dates and the detailed training schedule
              will be communicated to selected participants.
            </p>
          </Reveal>
        </section>

        {/* Important Conditions */}
        <section id="conditions" style={sectionStyle}>
          <Reveal>
            <h2 style={sectionTitleStyle}>Important Conditions</h2>
          </Reveal>
          <ul style={styles.arrowList}>
            {[
              "Applicants must satisfy the maximum age limit of 45 years on the date of application.",
              "Selected participants are expected to attend the full training programme and comply with host institution rules.",
              "Financial support is subject to approved MAHARISHI norms and availability of funds.",
              "Participants are responsible for providing accurate information and obtaining all documents required for international travel.",
              "Expenses not specifically covered under approved financial provisions are the participant's responsibility unless otherwise authorized.",
              "The decision of the selection committee regarding selection and financial support will be final.",
            ].map((item, i) => (
              <Reveal key={i} as="li" delay={i * 0.05} x={24} style={styles.arrowListItem}>
                <span style={styles.arrowIcon}>➜</span>
                <span>{item}</span>
              </Reveal>
            ))}
          </ul>
        </section>

        {/* Register (below Important Conditions) */}
        <section id="register" style={sectionStyle}>
          <Reveal>
            <div
              style={{
                ...styles.registerBox,
                padding: isMobile ? "28px 20px" : "40px 32px",
              }}
            >
              <p style={styles.registerEyebrow}>Apply Now</p>
              <h2
                style={{
                  ...styles.registerTitle,
                  fontSize: isMobile ? "22px" : "28px",
                }}
              >
                Ready to Register?
              </h2>
              <p
                style={{
                  ...styles.registerText,
                  fontSize: isMobile ? "14.5px" : "15.5px",
                }}
              >
                Please read the eligibility criteria and important conditions carefully, then
                complete the online registration form. Keep your passport, institutional and
                academic details ready.
              </p>
              <a
                href={GOOGLE_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  ...styles.registerButton,
                  width: isMobile ? "100%" : "auto",
                  boxSizing: "border-box",
                }}
              >
                Register Now <span aria-hidden="true">→</span>
              </a>
              <p style={styles.registerNote}>Opens the registration form in a new tab.</p>
            </div>
          </Reveal>
        </section>

        {/* Queries */}
        <section id="apply" style={{ ...sectionStyle, marginBottom: 0 }}>
          <Reveal delay={0.05}>
            <div style={styles.infoRow}>
              <div style={styles.infoCard}>
                <p style={styles.infoLabel}>Any Queries Contact</p>
                <p style={{ ...styles.infoValue, fontSize: isMobile ? "14.5px" : "16px" }}>
                  maharishi.iimr@gmail.com
                </p>
                <p
                  style={{
                    ...styles.infoValue,
                    fontSize: isMobile ? "14.5px" : "16px",
                    marginTop: 4,
                  }}
                >
                  maharishi@millets.res.in
                </p>
              </div>
            </div>
          </Reveal>
        </section>
      </div>
    </div>
  );
}

// =====================================================================
// Inline styles
// =====================================================================
const styles = {
  page: {
    fontFamily: "'Segoe UI', Arial, sans-serif",
    color: "#1f2d24",
    backgroundColor: "#fbfaf7",
    width: "100%",
    // "clip" stops sideways scrolling WITHOUT breaking position: sticky
    overflowX: "clip",
  },
  hero: {
    position: "relative",
    background: "linear-gradient(135deg, #2f6b3a 0%, #4c8c53 60%, #6ba86f 100%)",
    color: "#ffffff",
    textAlign: "center",
    overflow: "hidden",
  },
  heroPatternTop: {
    position: "absolute",
    top: -60,
    right: -60,
    width: 220,
    height: 220,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.08)",
  },
  heroPatternBottom: {
    position: "absolute",
    bottom: -80,
    left: -80,
    width: 260,
    height: 260,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.06)",
  },
  heroInner: {
    position: "relative",
    zIndex: 1,
    maxWidth: 780,
    margin: "0 auto",
  },
  heroEyebrow: {
    display: "inline-block",
    textTransform: "uppercase",
    letterSpacing: "2px",
    fontSize: "12px",
    fontWeight: 700,
    margin: "0 0 16px",
    padding: "6px 14px",
    borderRadius: "999px",
    backgroundColor: "rgba(255,255,255,0.16)",
  },
  heroTitle: {
    fontSize: "clamp(24px, 5.5vw, 42px)",
    fontWeight: 800,
    margin: "0 auto 16px",
    lineHeight: 1.25,
    wordBreak: "break-word",
  },
  heroSubtitle: {
    maxWidth: "650px",
    margin: "0 auto",
    opacity: 0.95,
    lineHeight: 1.6,
  },
  factsStripOuter: {
    backgroundColor: "#fbfaf7",
    display: "flex",
    justifyContent: "center",
  },
  factsStrip: {
    display: "flex",
    flexWrap: "wrap",
    gap: "14px",
    justifyContent: "center",
    maxWidth: "1000px",
    width: "100%",
    marginBottom: "20px",
    position: "relative",
    zIndex: 2,
  },
  factCard: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    backgroundColor: "#ffffff",
    borderRadius: "14px",
    padding: "16px 18px",
    boxShadow: "0 8px 22px rgba(47,107,58,0.12)",
    border: "1px solid #eee8d8",
    height: "100%",
    boxSizing: "border-box",
  },
  factIcon: {
    fontSize: "26px",
    lineHeight: 1,
  },
  factLabel: {
    fontSize: "11.5px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "#7a8577",
    fontWeight: 700,
    margin: "0 0 3px",
  },
  factValue: {
    fontSize: "14.5px",
    fontWeight: 700,
    color: "#1f2d24",
    margin: 0,
    lineHeight: 1.3,
  },
  quickNavOuter: {
    display: "flex",
    justifyContent: "center",
    zIndex: 20,
    boxSizing: "border-box",
    backgroundColor: "#fbfaf7",
  },
  quickNav: {
    display: "flex",
    gap: "8px",
    maxWidth: "900px",
    padding: "10px 0",
    borderBottom: "1px solid #e8e2d2",
    width: "100%",
  },
  quickNavPill: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#2f6b3a",
    backgroundColor: "#eef4ec",
    border: "1px solid #d7e6d5",
    borderRadius: "999px",
    padding: "8px 16px",
    cursor: "pointer",
  },
  quickNavPillHighlight: {
    color: "#ffffff",
    backgroundColor: "#2f6b3a",
    border: "1px solid #2f6b3a",
  },
  container: {
    maxWidth: "900px",
    margin: "0 auto",
    boxSizing: "border-box",
  },
  section: {},
  sectionTitle: {
    fontWeight: 800,
    color: "#22521f",
    margin: "0 0 20px",
    paddingBottom: "10px",
    borderBottom: "3px solid #dcecd6",
    display: "inline-block",
  },
  paragraph: {
    margin: "0 0 14px",
    color: "#3a423b",
  },
  arrowList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  arrowListItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    fontSize: "15px",
    lineHeight: 1.7,
    marginBottom: "12px",
    color: "#3a423b",
  },
  arrowIcon: {
    color: "#2f6b3a",
    fontWeight: 800,
    fontSize: "15px",
    flexShrink: 0,
    marginTop: "3px",
  },
  checklistGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))",
    gap: "12px",
  },
  checkItem: {
    display: "flex",
    gap: "10px",
    alignItems: "flex-start",
    backgroundColor: "#ffffff",
    border: "1px solid #eae4d4",
    borderRadius: "10px",
    padding: "14px 16px",
    boxShadow: "0 3px 10px rgba(0,0,0,0.03)",
    height: "100%",
    boxSizing: "border-box",
  },
  checkMark: {
    color: "#2f6b3a",
    fontWeight: 800,
    fontSize: "15px",
    flexShrink: 0,
  },
  checkText: {
    fontSize: "14.5px",
    lineHeight: 1.6,
    color: "#3a423b",
  },
  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))",
    gap: "16px",
  },
  trainingCard: {
    backgroundColor: "#ffffff",
    border: "1px solid #eae4d4",
    borderRadius: "14px",
    padding: "20px",
    boxShadow: "0 6px 16px rgba(47,107,58,0.06)",
    height: "100%",
    boxSizing: "border-box",
  },
  trainingCardIcon: {
    fontSize: "28px",
    marginBottom: "10px",
  },
  trainingCardText: {
    fontSize: "14.5px",
    lineHeight: 1.6,
    color: "#3a423b",
    margin: 0,
  },
  registerBox: {
    textAlign: "center",
    background: "linear-gradient(135deg, #2f6b3a 0%, #4c8c53 100%)",
    color: "#ffffff",
    borderRadius: "18px",
    boxShadow: "0 12px 32px rgba(47,107,58,0.28)",
    boxSizing: "border-box",
  },
  registerEyebrow: {
    display: "inline-block",
    textTransform: "uppercase",
    letterSpacing: "2px",
    fontSize: "12px",
    fontWeight: 700,
    margin: "0 0 12px",
    padding: "5px 14px",
    borderRadius: "999px",
    backgroundColor: "rgba(255,255,255,0.18)",
  },
  registerTitle: {
    fontWeight: 800,
    margin: "0 0 12px",
  },
  registerText: {
    maxWidth: "560px",
    margin: "0 auto 24px",
    lineHeight: 1.7,
    opacity: 0.95,
  },
  registerButton: {
    display: "inline-block",
    backgroundColor: "#ffffff",
    color: "#2f6b3a",
    fontWeight: 800,
    fontSize: "16px",
    padding: "15px 38px",
    borderRadius: "999px",
    textDecoration: "none",
    boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
    cursor: "pointer",
  },
  registerNote: {
    fontSize: "12.5px",
    opacity: 0.85,
    margin: "14px 0 0",
  },
  infoRow: {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",
  },
  infoCard: {
    flex: "1 1 220px",
    background: "linear-gradient(135deg, #eaf3e8, #f4f0e3)",
    borderRadius: "12px",
    padding: "18px 20px",
    border: "1px solid #dce8da",
    boxSizing: "border-box",
    minWidth: 0,
  },
  infoLabel: {
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "1px",
    color: "#2f6b3a",
    fontWeight: 800,
    margin: "0 0 8px",
  },
  infoValue: {
    fontWeight: 700,
    margin: 0,
    color: "#1f2d24",
    overflowWrap: "anywhere",
  },
};